import type {
    ComposableOptionsBase
} from '~/types';
import { useValibotUtils } from '~/composables';
import * as v from 'valibot';
import { fieldModelErrors as fErr } from '~/constants';

export interface UseAddressModelOptions extends ComposableOptionsBase {
    requireCompany?: boolean;
    requireEmail?: boolean;
    requirePhone?: boolean;
}

export function useAddressModel(options: Partial<UseAddressModelOptions> = {}) {
    const { vNullableDefaultString } = useValibotUtils();
    const { requireCompany, requireEmail, requirePhone } = options;

    const valFieldCompany = v.pipe(
        v.string(fErr.company),
        v.trim(),
        v.minLength(1, fErr.company)
    );

    const valFieldEmail = v.pipe(
        v.string(fErr.email),
        v.trim(),
        v.email(fErr.email)
    );

    const valFieldPhone = v.pipe(
        v.string(fErr.phone),
        v.trim(),
        v.minLength(1, fErr.phone)
    );

    const baseFormModel = v.object({
        uid: v.optional(
            v.string()
        ),
        isShipping: v.optional(
            v.boolean()
        ),
        isBilling: v.optional(
            v.boolean()
        ),
        label: v.nullish(
            v.string()
        ),
        firstName: v.pipe(
            v.string(fErr.firstName),
            v.minLength(1, fErr.firstName)
        ),
        lastName: v.pipe(
            v.string(fErr.lastName),
            v.minLength(1, fErr.lastName)
        ),

        company: requireCompany ? valFieldCompany : vNullableDefaultString(valFieldCompany),
        email: requireEmail ? valFieldEmail : vNullableDefaultString(valFieldEmail),
        phone: requirePhone ? valFieldPhone : vNullableDefaultString(valFieldPhone)
    });

    const addressModel = v.object({
        street1: v.pipe(
            v.string(fErr.street1),
            v.minLength(1, fErr.street1)
        ),
        street2: v.nullish(
            v.string()
        ),
        street3: v.nullish(
            v.string()
        ),
        city: v.pipe(
            v.string(fErr.city),
            v.minLength(1, fErr.city)
        ),
        state: v.pipe(
            v.string(fErr.state),
            v.minLength(1, fErr.state)
        ),
        country: v.pipe(
            v.string(fErr.country),
            v.minLength(1, fErr.country)
        ),
        postalCode: v.pipe(
            v.string(fErr.postalCode),
            v.minLength(1, fErr.postalCode)
        ),
        isResidential: v.optional(
            v.nullable(
                v.boolean()
            ),
            null
        )
    });

    const formModel = v.object({
        ...baseFormModel.entries,
        ...addressModel.entries}
    );
    
    return formModel;
}
